import { useFormik } from "formik";
import { FC, useMemo, useRef } from "react";

import Button from "components/Button";
import CertificateUpload from "components/CertificateUpload";
import DatePicker from "components/DatePicker";
import TextInput from "components/TextInput";

import useValidation from "hooks/useValidation";

import { validationSchema } from "../validationSchema";

import styles from "../styles.module.scss";

export type CalibrationDeviceInfo = {
  model?: string;
  eui?: string;
  /** Display-ready date (e.g. "01.01.2026"). */
  lastCalibration?: string;
  /** Display-ready date (e.g. "01.01.2027"). */
  nextCalibrationDue?: string;
};

export type CalibrationFormValues = {
  calibrationDate: string;
  calibratedBy: string;
  certificate: File | null;
};

type PropTypes = {
  deviceInfo?: CalibrationDeviceInfo;
  onClose?: () => void;
  onSubmit?: (values: CalibrationFormValues) => void;
  isLoading?: boolean;
};

const pad = (n: number) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const ModalMarkCalibratedContent: FC<PropTypes> = ({
  deviceInfo,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const formRef = useRef<HTMLDivElement>(null);

  const form = useFormik<CalibrationFormValues>({
    initialValues: {
      calibrationDate: todayISO(),
      calibratedBy: "",
      certificate: null,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      onSubmit?.({ ...values, calibratedBy: values.calibratedBy.trim() });
    },
  });

  useValidation(formRef, form.errors);

  const infoLines = useMemo(
    () => [
      { label: "Model", value: deviceInfo?.model },
      { label: "EUI", value: deviceInfo?.eui },
      { label: "Last calibration", value: deviceInfo?.lastCalibration },
      { label: "Next calibration due", value: deviceInfo?.nextCalibrationDue },
    ],
    [deviceInfo],
  );

  return (
    <div ref={formRef} className={styles.content}>
      <form className={styles.form} onSubmit={form.handleSubmit}>
        <div className={styles.info}>
          <p className={styles.infoTitle}>Information</p>
          <ul className={styles.infoList}>
            {infoLines.map((line) => (
              <li key={line.label}>
                {line.label}: {line.value || "—"}
              </li>
            ))}
          </ul>
          <span className={styles.infoBar} />
        </div>

        <div className={styles.fields}>
          <DatePicker
            label="Calibration date"
            placeholder="DD.MM.YYYY"
            value={form.values.calibrationDate}
            onChange={(value) => form.setFieldValue("calibrationDate", value)}
            error={form.errors.calibrationDate}
            maxDate={new Date()}
            isRequired
            isDisabled={isLoading}
          />

          <TextInput
            id="calibratedBy"
            name="calibratedBy"
            label="Calibrated by"
            placeholder="Calibrated by"
            value={form.values.calibratedBy}
            onChange={form.handleChange}
            error={form.errors.calibratedBy}
            isDisabled={isLoading}
            maxLength={100}
          />
        </div>

        <CertificateUpload
          value={form.values.certificate}
          onChange={(file) => form.setFieldValue("certificate", file)}
          isDisabled={isLoading}
        />

        <div className={styles.buttons}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ModalMarkCalibratedContent;
